import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageShell from "../components/PageShell";
import TextField from "../components/TextField";
import FormStatus from "../components/FormStatus";
import useSeo from "../hooks/useSeo";
import { api } from "../lib/api";
import { images } from "../data/content";

const initialState = {
  full_name: "",
  email: "",
  phone: "",
  role_interest: "",
  experience_level: "",
  location: "",
  linkedin_url: "",
  resume_summary: "",
  availability: "",
};

export default function CandidateForm() {
  useSeo("Candidate Registration", "Register with TalentBridge Consulting for career representation and targeted recruitment opportunities.");
  const [form, setForm] = useState(initialState);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const onChange = (event) => setForm({ ...form, [event.target.name]: event.target.value });
  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      await api.candidate(form);
      setForm(initialState);
      setStatus("success");
    } catch (error) {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <section className="bg-white px-5 pb-16 pt-32 md:px-8 lg:pb-24" data-testid="candidate-page-section">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#0066FF]" data-testid="candidate-eyebrow">For candidates</p>
            <h1 className="mt-5 font-heading text-4xl font-black leading-tight text-[#0A2540] sm:text-5xl" data-testid="candidate-title">Register for curated opportunities.</h1>
            <p className="mt-6 text-base leading-8 text-[#475569]" data-testid="candidate-description">
              Share your goals and experience. TalentBridge will add your profile to the candidate pipeline for screening and matching.
            </p>
            <Link to="/resume-checker" className="mt-6 inline-flex items-center gap-2 border border-[#0A2540] px-5 py-3 text-sm font-bold text-[#0A2540] hover:bg-[#0A2540] hover:text-white" data-testid="candidate-resume-checker-link">
              Check your resume first <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/resume-tailor" className="ml-0 mt-3 inline-flex items-center gap-2 bg-[#0066FF] px-5 py-3 text-sm font-bold text-white hover:bg-[#0052CC] md:ml-3 md:mt-6" data-testid="candidate-resume-tailor-link">
              <span className="bg-white px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-[#0066FF]" data-testid="candidate-resume-tailor-new-badge">New</span>
              Tailor resume to job <ArrowRight className="h-4 w-4" />
            </Link>
            <img src={images.team} alt="Professional team discussion for candidate opportunities" className="mt-10 aspect-[4/3] w-full border border-[#E2E8F0] object-cover" data-testid="candidate-image" />
          </div>
          <form onSubmit={onSubmit} className="grid gap-5 border border-[#E2E8F0] bg-[#FBFBFC] p-6 md:p-8" data-testid="candidate-registration-form">
            <FormStatus status={status} successText="Candidate registration saved to the CRM pipeline." />
            <div className="grid gap-5 md:grid-cols-2">
              <TextField id="full_name" label="Full name" value={form.full_name} onChange={onChange} />
              <TextField id="email" label="Email" type="email" value={form.email} onChange={onChange} />
              <TextField id="phone" label="Phone" value={form.phone} onChange={onChange} />
              <TextField id="role_interest" label="Role interest" value={form.role_interest} onChange={onChange} />
              <TextField id="experience_level" label="Experience level" value={form.experience_level} onChange={onChange} />
              <TextField id="location" label="Location" value={form.location} onChange={onChange} />
            </div>
            <TextField id="linkedin_url" label="LinkedIn URL" value={form.linkedin_url} onChange={onChange} required={false} />
            <TextField id="resume_summary" label="Career summary" multiline value={form.resume_summary} onChange={onChange} />
            <TextField id="availability" label="Availability" value={form.availability} onChange={onChange} />
            <button type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 bg-[#0A2540] px-6 py-4 font-bold text-white transition-transform hover:-translate-y-0.5 hover:bg-[#051424] disabled:opacity-60" data-testid="candidate-submit-button">
              {loading ? "Saving..." : "Register profile"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </div>
      </section>
    </PageShell>
  );
}